import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-language";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { JButton } from "@/components/JButton";
import { ArrowLeft, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute("/_authenticated/app/child/new")({
  component: AddChild,
});

const TIERS = [
  { key: "4-6" as const, ar: "براعم", en: "Buds", emoji: "🌱", min: 4, max: 6 },
  { key: "7-9" as const, ar: "أغصان", en: "Branches", emoji: "🌿", min: 7, max: 9 },
  { key: "10-12" as const, ar: "جذور", en: "Roots", emoji: "🌳", min: 10, max: 12 },
] as const;

const AVATARS = ["🌱", "🦊", "🦉", "🌙", "🌳", "🏮"];

const LIMITS: Record<"free" | "family" | "premium", number> = {
  free: 1,
  family: 3,
  premium: 6,
};

const schema = z.object({
  name: z.string().trim().min(1).max(40),
  age: z.number().int().min(4).max(12),
});

function AddChild() {
  const { user } = useAuth();
  const { lang, dir } = useLang();
  const { subscription, loading: subLoading } = useSubscription();
  const navigate = useNavigate();
  const isAr = lang === "ar";

  const [count, setCount] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [tier, setTier] = useState<(typeof TIERS)[number]>(TIERS[0]);
  const [age, setAge] = useState(5);
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("children")
      .select("id", { count: "exact", head: true })
      .eq("parent_id", user.id)
      .then(({ count }) => setCount(count ?? 0));
  }, [user]);

  useEffect(() => setAge(tier.min), [tier]);

  const userTier = (subscription?.tier ?? "free") as "free" | "family" | "premium";
  const limit = LIMITS[userTier];
  const atLimit = useMemo(() => count !== null && count >= limit, [count, limit]);

  const submit = async () => {
    if (!user) return;
    if (atLimit) {
      toast.error(
        isAr
          ? `وصلت للحد الأقصى (${limit}). رقّي اشتراكك لإضافة المزيد.`
          : `You've reached the ${limit}-child limit. Upgrade to add more.`,
      );
      return;
    }
    const parsed = schema.safeParse({ name, age });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setBusy(true);
    const { data, error } = await supabase
      .from("children")
      .insert({
        parent_id: user.id,
        name: parsed.data.name,
        age: parsed.data.age,
        age_tier: tier.key,
        avatar_emoji: avatar,
      })
      .select("id")
      .single();
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(isAr ? "تمّت إضافة الطفل! 🎉" : "Child added! 🎉");
    navigate({ to: "/app/child/$childId", params: { childId: (data as { id: string }).id } });
  };

  if (count === null || subLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="size-10 rounded-full border-4 border-clay border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <div dir={dir} className="px-5 py-8 max-w-xl mx-auto">
      <button
        onClick={() => navigate({ to: "/app" })}
        className="flex items-center gap-2 text-sm font-bold text-foreground/60 mb-4 hover:text-foreground"
      >
        <ArrowLeft className={`size-4 ${isAr ? "rotate-180" : ""}`} />
        {isAr ? "رجوع" : "Back"}
      </button>

      <h1 className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl font-bold mb-2`}>
        {isAr ? "أضف طفلاً" : "Add a child"}
      </h1>
      <p className="text-foreground/60 mb-1 text-sm">
        {isAr
          ? `لديك ${count} من ${limit} (خطة ${userTier === "free" ? "مجانية" : userTier === "family" ? "عائلة" : "مميزة"})`
          : `You have ${count} of ${limit} (${userTier} plan)`}
      </p>

      {atLimit ? (
        <div className="mt-6 rounded-3xl border-2 border-accent/40 bg-gradient-to-br from-accent/10 to-primary/10 p-6 text-center">
          <Lock className="mx-auto mb-3 size-8 text-accent" />
          <h2 className={`${isAr ? "font-display-ar" : "font-display-en"} text-xl font-bold mb-2`}>
            {isAr ? "وصلت للحد الأقصى" : "You've reached your limit"}
          </h2>
          <p className="text-sm text-foreground/70 mb-5">
            {isAr
              ? `خطتك الحالية تسمح بـ ${limit} ${limit === 1 ? "طفل" : "أطفال"}. رقّي اشتراكك لإضافة المزيد.`
              : `Your current plan allows ${limit} ${limit === 1 ? "child" : "children"}. Upgrade to add more.`}
          </p>
          <Link
            to="/app/pricing"
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-6 py-3 font-bold text-primary-foreground shadow-press hover:-translate-y-0.5 transition-all"
          >
            <Sparkles className="size-4" />
            {isAr ? "ترقية الاشتراك" : "Upgrade plan"}
          </Link>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <div>
            <label className="block text-sm font-bold mb-2">
              {isAr ? "اسم الطفل" : "Child's name"}
            </label>
            <input
              autoFocus
              type="text"
              dir={dir}
              placeholder={isAr ? "مثال: آدم" : "e.g. Adam"}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              className="w-full px-5 py-4 rounded-2xl border-2 border-clay/40 bg-card focus:border-primary focus:outline-none text-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-3">
              {isAr ? "الفئة العمرية" : "Age group"}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {TIERS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTier(t)}
                  className={`p-4 rounded-2xl border-2 text-center transition-all ${
                    tier.key === t.key
                      ? "border-primary bg-primary/10 scale-105 shadow-lg"
                      : "border-clay/40 bg-card hover:border-clay"
                  }`}
                >
                  <div className="text-3xl mb-1">{t.emoji}</div>
                  <div className={`${isAr ? "font-display-ar" : "font-display-en"} text-base font-bold text-primary`}>
                    {isAr ? t.ar : t.en}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">
              {isAr ? "العمر" : "Age"}: <span className="text-primary">{age}</span>
            </label>
            <input
              type="range"
              min={tier.min}
              max={tier.max}
              value={age}
              onChange={(e) => setAge(Number(e.target.value))}
              className="w-full accent-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-3">
              {isAr ? "اختر شخصية" : "Pick an avatar"}
            </label>
            <div className="grid grid-cols-6 gap-2.5">
              {AVATARS.map((a) => (
                <button
                  key={a}
                  onClick={() => setAvatar(a)}
                  className={`aspect-square text-3xl rounded-2xl border-2 flex items-center justify-center transition-all ${
                    avatar === a
                      ? "border-primary bg-primary/15 scale-110"
                      : "border-clay/40 bg-card hover:border-clay/70"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          <JButton size="lg" className="w-full" onClick={submit} disabled={busy || !name.trim()}>
            {busy ? (isAr ? "جارٍ الحفظ…" : "Saving…") : isAr ? "إضافة الطفل" : "Add child"}
          </JButton>
        </div>
      )}
    </div>
  );
}
